import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Box, Button, Card, Flex, FormControl, FormLabel, HStack, IconButton,
  Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Stack, Text, Textarea, useDisclosure, useToast, VStack,
} from '@chakra-ui/react';
import { MdAdd, MdArrowDownward, MdArrowUpward, MdDelete, MdEdit } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useContentTree, useCreateEntry, useDeleteEntry, useReorderEntry } from './hooks/useContent';
import type { ContentTreeNodeDto } from './api/contentApi';

function statusColor(status: string) {
  return status === 'published' ? 'green' : 'gray';
}

export function ContentListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: tree, isLoading } = useContentTree();

  const deleteEntry = useDeleteEntry();
  const reorderEntry = useReorderEntry();

  const addDisclosure = useDisclosure();
  const [addCollection, setAddCollection] = useState<ContentTreeNodeDto | null>(null);

  const openAdd = (collection: ContentTreeNodeDto) => {
    setAddCollection(collection);
    addDisclosure.onOpen();
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(t('admin.content.confirmDeleteEntry', { title }))) return;
    try {
      await deleteEntry.mutateAsync(id);
      toast({ title: t('admin.content.entryDeleted'), status: 'success', duration: 3000 });
    } catch {
      toast({ title: t('admin.content.actionFailed'), status: 'error', duration: 5000 });
    }
  };

  const move = async (entries: ContentTreeNodeDto[], index: number, dir: -1 | 1) => {
    const neighbor = entries[index + dir];
    if (!neighbor) return;
    const newSort = dir === -1 ? neighbor.node.sortOrder - 1 : neighbor.node.sortOrder + 1;
    try {
      await reorderEntry.mutateAsync({ id: entries[index].node.id, sortOrder: newSort });
    } catch {
      toast({ title: t('admin.content.actionFailed'), status: 'error', duration: 5000 });
    }
  };

  if (isLoading) {
    return <Flex justify="center" py="60px"><Spinner /></Flex>;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Stack spacing="16px">
        {(tree ?? []).map((item) => (
          <Card key={item.node.id} p="20px">
            <Flex align="center" gap="12px" wrap="wrap">
              <Box>
                <HStack>
                  <Text fontWeight="700" fontSize="md">{item.node.name}</Text>
                  <Badge colorScheme="purple">{item.node.type}</Badge>
                  <Badge colorScheme={statusColor(item.node.status)}>{item.node.status}</Badge>
                </HStack>
                <Text fontSize="sm" color="secondaryGray.600">/{item.node.slug}</Text>
              </Box>
              <HStack ml="auto" spacing="6px">
                {item.languages.map((l) => (
                  <Badge key={l.languageCode} variant="subtle" colorScheme={l.hasContent ? 'blue' : 'red'}>
                    {l.languageCode.toUpperCase()}
                  </Badge>
                ))}
                <Button size="sm" leftIcon={<MdEdit />} onClick={() => navigate(`/content/${item.node.id}`)}>
                  {t('common.edit')}
                </Button>
              </HStack>
            </Flex>

            {item.node.type === 'collection' && (
              <Box mt="14px" pl="14px" borderLeftWidth="2px" borderColor="secondaryGray.300">
                <Flex align="center" mb="8px">
                  <Text fontSize="sm" fontWeight="600" color="secondaryGray.700">
                    {t('admin.content.entries')} ({item.children.length})
                  </Text>
                  <Button size="xs" ml="auto" leftIcon={<MdAdd />} variant="outline" onClick={() => openAdd(item)}>
                    {t('admin.content.addEntry')}
                  </Button>
                </Flex>
                <Stack spacing="6px">
                  {item.children.map((entry, idx) => (
                    <Flex key={entry.node.id} align="center" gap="8px" py="4px">
                      <Text fontSize="sm" noOfLines={1}>{entry.node.name}</Text>
                      <Badge variant="subtle" colorScheme={statusColor(entry.node.status)} fontSize="0.65rem">{entry.node.status}</Badge>
                      <HStack ml="auto" spacing="2px">
                        <IconButton aria-label="up" icon={<MdArrowUpward />} size="xs" variant="ghost" isDisabled={idx === 0} onClick={() => move(item.children, idx, -1)} />
                        <IconButton aria-label="down" icon={<MdArrowDownward />} size="xs" variant="ghost" isDisabled={idx === item.children.length - 1} onClick={() => move(item.children, idx, 1)} />
                        <IconButton aria-label="edit" icon={<MdEdit />} size="xs" variant="ghost" onClick={() => navigate(`/content/${entry.node.id}`)} />
                        <IconButton aria-label="delete" icon={<MdDelete />} size="xs" variant="ghost" colorScheme="red" onClick={() => handleDelete(entry.node.id, entry.node.name)} />
                      </HStack>
                    </Flex>
                  ))}
                  {item.children.length === 0 && (
                    <Text fontSize="sm" color="secondaryGray.600">{t('admin.content.noEntries')}</Text>
                  )}
                </Stack>
              </Box>
            )}
          </Card>
        ))}
      </Stack>

      {addCollection && (
        <AddEntryModal
          isOpen={addDisclosure.isOpen}
          onClose={addDisclosure.onClose}
          collection={addCollection}
        />
      )}
    </Box>
  );
}

function AddEntryModal({ isOpen, onClose, collection }: { isOpen: boolean; onClose: () => void; collection: ContentTreeNodeDto }) {
  const { t } = useTranslation();
  const toast = useToast();
  const createEntry = useCreateEntry();
  const [enTitle, setEnTitle] = useState('');
  const [enBody, setEnBody] = useState('');
  const [ukTitle, setUkTitle] = useState('');
  const [ukBody, setUkBody] = useState('');

  const reset = () => { setEnTitle(''); setEnBody(''); setUkTitle(''); setUkBody(''); };

  const submit = async () => {
    if (!enTitle.trim()) {
      toast({ title: t('admin.content.titleRequired'), status: 'warning', duration: 3000 });
      return;
    }
    const translations = [{ languageCode: 'en', title: enTitle, bodyHtml: `<p>${enBody}</p>`, summary: enBody }];
    if (ukTitle.trim()) translations.push({ languageCode: 'uk', title: ukTitle, bodyHtml: `<p>${ukBody}</p>`, summary: ukBody });
    try {
      await createEntry.mutateAsync({ collectionId: collection.node.id, req: { translations } });
      toast({ title: t('admin.content.entryCreated'), status: 'success', duration: 3000 });
      reset();
      onClose();
    } catch {
      toast({ title: t('admin.content.actionFailed'), status: 'error', duration: 5000 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('admin.content.addEntry')} — {collection.node.name}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing="14px" align="stretch">
            <FormControl isRequired>
              <FormLabel>{t('admin.content.questionEn')}</FormLabel>
              <Input value={enTitle} onChange={(e) => setEnTitle(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('admin.content.answerEn')}</FormLabel>
              <Textarea value={enBody} onChange={(e) => setEnBody(e.target.value)} rows={3} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('admin.content.questionUk')}</FormLabel>
              <Input value={ukTitle} onChange={(e) => setUkTitle(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>{t('admin.content.answerUk')}</FormLabel>
              <Textarea value={ukBody} onChange={(e) => setUkBody(e.target.value)} rows={3} />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="12px" onClick={onClose}>{t('common.cancel')}</Button>
          <Button colorScheme="purple" onClick={submit} isLoading={createEntry.isPending}>{t('common.save')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
